'use client'

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'

import { SettingsIcon } from '@/components/icons'
import { useMobileNavigation, type MobileNavigationState } from '@/components/MobileNavigationContext'
import AutoSaveHint from './auto-save-hint'
import { searchManufacturers } from '@/lib/api/client/manufacturers'
import type { ManufacturerSearchItem } from '@/lib/api/schemas/manufacturer-search.schema'
import {
  formatManufacturerSalesShare,
  loadManufacturerSalesShares,
  type ManufacturerSalesShares,
} from '@/lib/manufacturer-sales-share'
import {
  archiveGoldExhibition,
  archiveGoldExhibitionSupplier,
  createGoldExhibition,
  createGoldExhibitionPurchaseBatch,
  createGoldExhibitionSupplier,
  deleteGoldExhibitionPurchaseBatch,
  getGoldExhibition,
  listGoldExhibitionArchive,
  listGoldExhibitions,
  restoreGoldExhibition,
  restoreGoldExhibitionSupplier,
  updateGoldExhibition,
  updateGoldExhibitionPurchaseBatch,
  updateGoldExhibitionSupplier,
} from '@/lib/api/client/gold-exhibitions'
import type {
  GoldExhibitionDetail,
  GoldExhibitionArchivedRecord,
  GoldExhibitionPurchaseBatch,
  GoldExhibitionSupplier,
} from '@/lib/api/schemas/gold-exhibitions.schema'
import {
  formatKopecks,
  formatKopecksPerGram,
  formatMilliGrams,
  parseGramsInput,
  parseRublesPerGramInput,
} from '@/lib/gold-exhibition-purchase'

type PurchaseBatch = {
  id: string
  weightMilliGrams: number
  priceKopecksPerGram: number | null
  createdAt: string
  updatedAt?: string | null
  updatedByLabel?: string | null
}

type ExhibitionSupplier = {
  id: string
  name: string
  manufacturerId: string | null
  manufacturerDisplayName: string | null
  manufacturerAnalyticsKey: string | null
  priceKopecksPerGram: number | null
  createdAt: string
  batches: PurchaseBatch[]
}

type PrototypeExhibition = {
  id: string
  name: string
  budgetKopecks: number
  startsOn: string | null
  endsOn: string | null
  imageUrl: string | null
  createdAt: string
  suppliers: ExhibitionSupplier[]
}

type PrototypeState = {
  selectedExhibitionId: string
  exhibitions: PrototypeExhibition[]
}

type Screen =
  | { kind: 'home' }
  | { kind: 'archive' }
  | { kind: 'new-exhibition' }
  | { kind: 'exhibition'; exhibitionId: string }
  | { kind: 'exhibition-settings'; exhibitionId: string }
  | { kind: 'new-supplier'; exhibitionId: string }
  | { kind: 'supplier'; exhibitionId: string; supplierId: string }
  | { kind: 'new-batch'; exhibitionId: string; supplierId: string }
  | { kind: 'edit-batch'; exhibitionId: string; supplierId: string; batchId: string }
  | { kind: 'supplier-settings'; exhibitionId: string; supplierId: string }

type DestructiveConfirmation =
  | { kind: 'delete-batch'; exhibitionId: string; supplierId: string; supplierName: string; batch: PurchaseBatch }
  | { kind: 'archive-exhibition'; exhibitionId: string; exhibitionName: string }
  | { kind: 'archive-supplier'; exhibitionId: string; supplierId: string; supplierName: string }

const GOLD_EXHIBITION_ROOT = '/gold-exhibition'
const SUPPLIER_COLORS = ['#2563EB', '#DC2626', '#059669', '#D97706', '#7C3AED', '#0891B2', '#DB2777', '#4D7C0F', '#EA580C', '#4338CA', '#0F766E', '#BE123C', '#A16207', '#6D28D9', '#0284C7', '#15803D', '#C2410C', '#7E22CE', '#0369A1', '#B91C1C'] as const

function prototypePath(...segments: string[]): string {
  return [GOLD_EXHIBITION_ROOT, ...segments.map(encodeURIComponent)].join('/')
}

function screenFromPath(pathname: string): Screen {
  const suffix = pathname.startsWith(GOLD_EXHIBITION_ROOT) ? pathname.slice(GOLD_EXHIBITION_ROOT.length) : ''
  const segments = suffix.split('/').filter(Boolean).map(decodeURIComponent)
  if (segments.length === 0) return { kind: 'home' }
  if (segments.length === 1 && segments[0] === 'new') return { kind: 'new-exhibition' }
  if (segments.length === 1 && segments[0] === 'archive') return { kind: 'archive' }
  if (segments.length === 1) return { kind: 'exhibition', exhibitionId: segments[0] }
  if (segments.length === 2 && segments[1] === 'settings') {
    return { kind: 'exhibition-settings', exhibitionId: segments[0] }
  }
  if (segments.length === 3 && segments[1] === 'suppliers' && segments[2] === 'new') {
    return { kind: 'new-supplier', exhibitionId: segments[0] }
  }
  if (segments.length === 3 && segments[1] === 'suppliers') {
    return { kind: 'supplier', exhibitionId: segments[0], supplierId: segments[2] }
  }
  if (segments.length === 4 && segments[1] === 'suppliers' && segments[3] === 'settings') {
    return { kind: 'supplier-settings', exhibitionId: segments[0], supplierId: segments[2] }
  }
  if (segments.length === 4 && segments[1] === 'suppliers' && segments[3] === 'new') {
    return { kind: 'new-batch', exhibitionId: segments[0], supplierId: segments[2] }
  }
  if (segments.length === 6 && segments[1] === 'suppliers' && segments[3] === 'batches' && segments[5] === 'edit') {
    return { kind: 'edit-batch', exhibitionId: segments[0], supplierId: segments[2], batchId: segments[4] }
  }
  return { kind: 'home' }
}

type ExhibitionTiming = 'current' | 'future' | 'past' | 'undated'

function localCalendarDate(date = new Date()): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function exhibitionTiming(exhibition: Pick<PrototypeExhibition, 'startsOn' | 'endsOn'>, today: string): ExhibitionTiming {
  if (exhibition.startsOn === null && exhibition.endsOn === null) return 'undated'
  if (exhibition.startsOn !== null && exhibition.startsOn > today) return 'future'
  if (exhibition.endsOn !== null && exhibition.endsOn < today) return 'past'
  return 'current'
}

function groupedExhibitions(exhibitions: PrototypeExhibition[], today: string): Record<ExhibitionTiming, PrototypeExhibition[]> {
  const groups: Record<ExhibitionTiming, PrototypeExhibition[]> = { current: [], future: [], past: [], undated: [] }
  exhibitions.forEach((exhibition) => groups[exhibitionTiming(exhibition, today)].push(exhibition))
  groups.current.sort((left, right) => (right.startsOn ?? '').localeCompare(left.startsOn ?? ''))
  groups.future.sort((left, right) => (left.startsOn ?? '').localeCompare(right.startsOn ?? ''))
  groups.past.sort((left, right) => (right.endsOn ?? right.startsOn ?? '').localeCompare(left.endsOn ?? left.startsOn ?? ''))
  groups.undated.sort((left, right) => right.createdAt.localeCompare(left.createdAt))
  return groups
}

const initialState: PrototypeState = {
  selectedExhibitionId: '',
  exhibitions: [],
}

function batchFromApi(batch: GoldExhibitionPurchaseBatch): PurchaseBatch {
  return {
    id: batch.id,
    weightMilliGrams: batch.weightMilliGrams,
    priceKopecksPerGram: batch.priceKopecksPerGram,
    createdAt: batch.createdAt,
    updatedAt: batch.updatedAt,
    updatedByLabel: batch.updatedBySubject,
  }
}

function supplierFromApi(supplier: GoldExhibitionSupplier): ExhibitionSupplier {
  return {
    id: supplier.id,
    name: supplier.name,
    manufacturerId: supplier.manufacturerId,
    manufacturerDisplayName: supplier.manufacturerDisplayName,
    manufacturerAnalyticsKey: supplier.manufacturerAnalyticsKey,
    priceKopecksPerGram: supplier.priceKopecksPerGram,
    createdAt: supplier.createdAt,
    batches: supplier.batches.map(batchFromApi),
  }
}

function fromApi(detail: GoldExhibitionDetail): PrototypeExhibition {
  return {
    id: detail.id,
    name: detail.name,
    budgetKopecks: Number(detail.budgetKopecks ?? '0'),
    startsOn: detail.startsOn,
    endsOn: detail.endsOn,
    imageUrl: detail.imageUrl,
    createdAt: detail.createdAt,
    suppliers: detail.suppliers.map(supplierFromApi),
  }
}

async function loadAllExhibitions(signal?: AbortSignal): Promise<PrototypeExhibition[]> {
  const details: PrototypeExhibition[] = []
  let cursor: string | undefined
  do {
    const response = await listGoldExhibitions(
      cursor === undefined ? { limit: 100 } : { limit: 100, cursor },
      signal,
    )
    details.push(...await Promise.all(
      response.items.map((item) => getGoldExhibition(item.id, signal).then(fromApi)),
    ))
    cursor = response.nextCursor ?? undefined
  } while (cursor !== undefined)
  return details
}

function supplierWeight(supplier: ExhibitionSupplier): bigint {
  return supplier.batches.reduce((total, batch) => total + BigInt(batch.weightMilliGrams), BigInt(0))
}

function amountFor(weightMilliGrams: bigint, priceKopecksPerGram: number | null): bigint | null {
  if (priceKopecksPerGram === null) return null
  return (weightMilliGrams * BigInt(priceKopecksPerGram) + BigInt(500)) / BigInt(1000)
}

function parseBudget(value: string): number | null {
  const normalized = value.trim().replaceAll(' ', '').replace(',', '.')
  if (!/^\d{1,9}(?:\.\d{1,2})?$/.test(normalized)) return null
  const [rubles, kopecks = ''] = normalized.split('.')
  const result = Number(rubles) * 100 + Number(kopecks.padEnd(2, '0'))
  return Number.isSafeInteger(result) && result > 0 ? result : null
}

function supplierAmount(supplier: ExhibitionSupplier): bigint {
  return supplier.batches.reduce(
    (total, batch) => total + (amountFor(BigInt(batch.weightMilliGrams), batch.priceKopecksPerGram) ?? BigInt(0)),
    BigInt(0),
  )
}

function supplierRateLabel(rate: number | null): string {
  return rate === null ? 'Не указана цена закупки за грамм' : formatKopecksPerGram(rate)
}

function purchaseCountLabel(count: number): string {
  const lastTwo = count % 100
  if (lastTwo >= 11 && lastTwo <= 14) return `${count} закупок`
  const last = count % 10
  if (last === 1) return `${count} закупка`
  if (last >= 2 && last <= 4) return `${count} закупки`
  return `${count} закупок`
}

function exhibitionDateLabel(startsOn: string | null, endsOn: string | null): string {
  if (startsOn === null && endsOn === null) return 'Даты не указаны'
  const format = (value: string) => new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`))
  if (startsOn === null) return `до ${format(endsOn!)}`
  if (endsOn === null) return `с ${format(startsOn)}`
  return `${format(startsOn)} — ${format(endsOn)}`
}

function totalsForExhibition(exhibition: PrototypeExhibition): { weight: bigint; amount: bigint } {
  return exhibition.suppliers.reduce(
    (total, item) => ({ weight: total.weight + supplierWeight(item), amount: total.amount + supplierAmount(item) }),
    { weight: BigInt(0), amount: BigInt(0) },
  )
}

function BackButton({ onClick }: { onClick: () => void }) {
  return <button type="button" onClick={onClick} aria-label="Назад" className="grid size-11 shrink-0 place-items-center rounded-full text-ink active:bg-bone"><svg aria-hidden="true" viewBox="0 0 24 24" className="size-8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg></button>
}

function ArchiveIcon() {
  return <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7.5h16v12H4z" /><path d="M3 4.5h18v3H3zM9 11.5h6" /></svg>
}

export function visualKeyboardInset(layoutHeight: number, visualHeight: number, offsetTop: number): number {
  return Math.max(0, Math.round(layoutHeight - visualHeight - offsetTop))
}

function useVisualKeyboardInset(): number {
  const [inset, setInset] = useState(0)
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return
    const update = () => setInset(visualKeyboardInset(window.innerHeight, viewport.height, viewport.offsetTop))
    update()
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
    }
  }, [])
  return inset
}

function BottomAction({ label, disabled = false, keyboardInset, onClick }: { label: string; disabled?: boolean; keyboardInset: number; onClick: () => void }) {
  return (
    <footer data-keyboard-aware-action style={{ transform: `translateY(-${keyboardInset}px)` }} className="z-30 w-full shrink-0 border-t border-rule bg-paper px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 transition-transform duration-150">
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className="min-h-12 w-full rounded-xl bg-onyx px-5 text-[16px] font-semibold text-white shadow-sm active:bg-ink-2 disabled:opacity-40"
      >
        {label}
      </button>
    </footer>
  )
}

function MutationAlert({ message }: { message: string | null }) {
  if (message === null) return null
  return <p role="alert" className="mx-4 mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-st-unpaid">{message}</p>
}

function DestructiveConfirmationDialog({ action, busy, error, onCancel, onConfirm }: {
  action: DestructiveConfirmation | null
  busy: boolean
  error: string | null
  onCancel: () => void
  onConfirm: () => void
}) {
  if (action === null || typeof document === 'undefined') return null

  const title = action.kind === 'delete-batch'
    ? 'Удалить закупку?'
    : action.kind === 'archive-exhibition'
      ? 'Переместить выставку в архив?'
      : 'Переместить поставщика в архив?'
  const description = action.kind === 'delete-batch'
    ? `Закупка ${formatMilliGrams(BigInt(action.batch.weightMilliGrams))} у «${action.supplierName}» будет удалена.`
    : action.kind === 'archive-exhibition'
      ? `Выставка «${action.exhibitionName}» останется в архиве и её можно будет восстановить.`
      : `Поставщик «${action.supplierName}» и его закупки останутся в архиве и их можно будет восстановить.`
  const confirmLabel = action.kind === 'delete-batch' ? 'Удалить закупку' : 'В архив'

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-end justify-center bg-black/40 p-3 sm:items-center sm:p-4">
      <section role="alertdialog" aria-modal="true" aria-labelledby="gold-exhibition-confirm-title" aria-describedby="gold-exhibition-confirm-description" className="w-full max-w-md rounded-2xl border border-rule bg-paper p-5 shadow-2xl">
        <h2 id="gold-exhibition-confirm-title" className="text-lg font-semibold text-ink">{title}</h2>
        <p id="gold-exhibition-confirm-description" className="mt-2 text-sm leading-5 text-ink-muted">{description}</p>
        {error !== null && <p role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-st-unpaid">{error}</p>}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button type="button" autoFocus disabled={busy} onClick={onCancel} className="min-h-11 rounded-xl border border-rule-strong px-4 text-sm font-semibold text-ink-2 disabled:opacity-40">Отмена</button>
          <button type="button" disabled={busy} onClick={onConfirm} className="min-h-11 rounded-xl bg-red-700 px-4 text-sm font-semibold text-white disabled:opacity-40">{busy ? 'Сохраняем…' : confirmLabel}</button>
        </div>
      </section>
    </div>,
    document.body,
  )
}

function PurchaseBatchEditorFields({ amount, baselineSpent, budget, inputId, onBlur, onChange, onRateBlur, onRateChange, rateValue, supplierColor, value }: { amount: bigint | null; baselineSpent: bigint; budget: bigint; inputId: string; onBlur?: () => void; onChange: (value: string) => void; onRateBlur?: () => void; onRateChange: (value: string) => void; rateValue: string; supplierColor: string; value: string }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const projectedSpent = baselineSpent + (amount ?? BigInt(0))
  const projectedRemaining = budget - projectedSpent
  const cappedBaselineSpent = budget > 0 && baselineSpent > budget ? budget : baselineSpent
  const cappedProjectedSpent = budget > 0 && projectedSpent > budget ? budget : projectedSpent
  const baselineBudgetWidth = budget > 0 ? Number((cappedBaselineSpent * BigInt(10000)) / budget) / 100 : 0
  const projectedBudgetWidth = budget > 0 ? Number((cappedProjectedSpent * BigInt(10000)) / budget) / 100 : 0
  const purchaseBudgetWidth = Math.max(0, projectedBudgetWidth - baselineBudgetWidth)
  const overBudgetWidth = budget > 0 && projectedSpent > budget
    ? Math.min(100, Number(((projectedSpent - budget) * BigInt(10000)) / budget) / 100)
    : 0

  useEffect(() => {
    inputRef.current?.focus()
  }, [inputId])

  return <>
    <label htmlFor={inputId} className="mb-2 block text-sm font-medium text-ink-2">Сколько купили, грамм</label>
    <input ref={inputRef} id={inputId} autoFocus inputMode="decimal" value={value} onBlur={onBlur} onChange={(event) => onChange(event.currentTarget.value)} className="min-h-16 w-full rounded-2xl border border-rule-strong bg-paper px-4 text-[28px] font-semibold tabular-nums outline-none focus:border-gold-deep focus:ring-2 focus:ring-gold/25" placeholder="0,000" />
    <div className="mt-4 flex items-end justify-between gap-4 border-t border-rule pt-4">
      <label className="min-w-0 flex-1 text-sm font-medium text-ink-2"><span data-purchase-rate-label>Цена за грамм, ₽</span>
        <input aria-label="Цена за грамм, ₽" inputMode="decimal" value={rateValue} onBlur={onRateBlur} onChange={(event) => onRateChange(event.currentTarget.value)} className="mt-2 block min-h-11 w-full max-w-44 rounded-xl border border-rule-strong bg-paper px-3 text-[18px] font-semibold tabular-nums outline-none focus:border-gold-deep focus:ring-2 focus:ring-gold/25" placeholder="0,00" />
      </label>
      <strong className="whitespace-nowrap pb-2 text-2xl tabular-nums">{amount === null ? '—' : formatKopecks(amount)}</strong>
    </div>
    <div className="mt-4 rounded-xl bg-bone/70 px-4 py-3.5 tabular-nums">
      <p className="text-sm font-medium text-ink-2">Оставшийся бюджет выставки</p>
      <div className="mt-1.5 flex items-baseline justify-between gap-3">
        <strong className={`text-[17px] font-semibold ${projectedRemaining < 0 ? 'text-st-unpaid' : 'text-ink'}`}>{budget === BigInt(0) ? 'Не задан' : projectedRemaining < 0 ? `Превышен на ${formatKopecks(-projectedRemaining)}` : formatKopecks(projectedRemaining)}</strong>
        {budget > 0 && <span className="text-sm text-ink-muted">из {formatKopecks(budget)}</span>}
      </div>
      {budget > 0 && <div data-budget-preview className="relative mt-3 flex h-2.5 overflow-hidden rounded-full bg-black/[0.07]" role="img" aria-label="Бюджет после этой закупки">
        <span className="h-full bg-ink/35" style={{ width: `${baselineBudgetWidth}%` }} />
        <span className="h-full opacity-80" style={{ width: `${purchaseBudgetWidth}%`, backgroundColor: supplierColor }} />
        {overBudgetWidth > 0 && <span data-budget-overflow className="absolute inset-y-0 right-0 bg-red-500/85" style={{ width: `${overBudgetWidth}%` }} />}
      </div>}
    </div>
  </>
}

function BatchActionsMenu({ author, created, onDelete, onEdit }: { author: string; created: string; onDelete: () => void; onEdit: () => void }) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const placeAbove = anchor !== null && window.innerHeight - anchor.bottom < 190
  const close = () => setAnchor(null)

  return <span className="mr-3 shrink-0">
    <button
      type="button"
      aria-label="Действия с закупкой"
      aria-expanded={anchor !== null}
      aria-haspopup="menu"
      onClick={(event) => {
        const nextAnchor = event.currentTarget.getBoundingClientRect()
        setAnchor((current) => current === null ? nextAnchor : null)
      }}
      className="grid size-9 place-items-center rounded-full text-ink-muted active:bg-bone focus:outline-none focus:ring-2 focus:ring-gold-deep"
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5" fill="currentColor"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
    </button>
    {anchor !== null && typeof document !== 'undefined' && createPortal(
      <>
        <button type="button" aria-label="Закрыть действия с закупкой" onClick={close} className="fixed inset-0 z-[99] cursor-default" />
        <div
          role="menu"
          aria-label="Действия с закупкой"
          className="fixed z-[100] w-56 overflow-hidden rounded-xl border border-rule bg-paper text-left shadow-xl"
          style={{
            right: `${Math.max(8, window.innerWidth - anchor.right)}px`,
            top: `${placeAbove ? anchor.top - 8 : anchor.bottom + 8}px`,
            transform: placeAbove ? 'translateY(-100%)' : undefined,
          }}
        >
          <div className="border-b border-rule px-4 py-3"><p className="text-[13px] font-medium text-ink-2">Добавил {author}</p><p className="mt-0.5 text-[13px] text-ink-muted">{created}</p></div>
          <button type="button" role="menuitem" onClick={() => { close(); onEdit() }} className="min-h-11 w-full px-4 text-left text-[16px] active:bg-bone">Изменить</button>
          <button type="button" role="menuitem" onClick={() => { close(); onDelete() }} className="min-h-11 w-full border-t border-rule px-4 text-left text-[16px] text-st-unpaid active:bg-red-50">Удалить</button>
        </div>
      </>,
      document.body,
    )}
  </span>
}

export default function GoldExhibitionSupplierPrototype() {
  const location = useLocation()
  const navigate = useNavigate()
  const { setNavigation } = useMobileNavigation()
  const [state, setState] = useState<PrototypeState>(initialState)
  const batchKeyboardPrimerRef = useRef<HTMLInputElement>(null)
  const supplierKeyboardPrimerRef = useRef<HTMLInputElement>(null)
  const supplierNameInputRef = useRef<HTMLInputElement>(null)
  const supplierRateInputRef = useRef<HTMLInputElement>(null)

  const navigateToBatchEditor = (path: string, inputId: string) => {
    batchKeyboardPrimerRef.current?.focus({ preventScroll: true })
    flushSync(() => navigate(path, { preventScrollReset: true }))
    const input = document.getElementById(inputId)
    if (input instanceof HTMLInputElement) input.focus({ preventScroll: true })
  }

  const navigateToSupplierCreation = (path: string) => {
    supplierKeyboardPrimerRef.current?.focus({ preventScroll: true })
    flushSync(() => navigate(path, { preventScrollReset: true }))
    supplierNameInputRef.current?.focus({ preventScroll: true })
  }
  const screen = useMemo(() => screenFromPath(location.pathname), [location.pathname])
  const [exhibitionName, setExhibitionName] = useState('')
  const [exhibitionBudget, setExhibitionBudget] = useState('')
  const [exhibitionStartsOn, setExhibitionStartsOn] = useState('')
  const [exhibitionEndsOn, setExhibitionEndsOn] = useState('')
  const [exhibitionImageUrl, setExhibitionImageUrl] = useState('')
  const [settingsName, setSettingsName] = useState('')
  const [settingsBudget, setSettingsBudget] = useState('')
  const [settingsStartsOn, setSettingsStartsOn] = useState('')
  const [settingsEndsOn, setSettingsEndsOn] = useState('')
  const [settingsImageUrl, setSettingsImageUrl] = useState('')
  const [supplierSettingsRate, setSupplierSettingsRate] = useState('')
  const [supplierSettingsRateSupplierId, setSupplierSettingsRateSupplierId] = useState<string | null>(null)
  const supplierSettingsRateDraftRef = useRef(supplierSettingsRate)
  supplierSettingsRateDraftRef.current = supplierSettingsRate
  const supplierRateSaveTimerRef = useRef<number | null>(null)
  const supplierRateSavePromiseRef = useRef<Promise<boolean> | null>(null)
  const supplierRatePersistedRef = useRef<{ supplierId: string; rate: number | null } | null>(null)
  const supplierRateSaveRef = useRef<(draft: string) => Promise<boolean>>(async () => true)
  const [supplierName, setSupplierName] = useState('')
  const [supplierManufacturerId, setSupplierManufacturerId] = useState<string | null>(null)
  const [supplierRate, setSupplierRate] = useState('')
  const [batchWeight, setBatchWeight] = useState('')
  const [batchRate, setBatchRate] = useState('')
  const [editBatchWeight, setEditBatchWeight] = useState('')
  const [editBatchRate, setEditBatchRate] = useState('')
  const batchDraftRef = useRef({ weight: editBatchWeight, rate: editBatchRate })
  batchDraftRef.current = { weight: editBatchWeight, rate: editBatchRate }
  const batchSaveTimerRef = useRef<number | null>(null)
  const batchSavePromiseRef = useRef<Promise<boolean> | null>(null)
  const batchPersistedRef = useRef<{ batchId: string; weightMilliGrams: number; priceKopecksPerGram: number | null } | null>(null)
  const batchSaveRef = useRef<(draft: { weight: string; rate: string }) => Promise<boolean>>(async () => true)
  const [swipedBatchId, setSwipedBatchId] = useState<string | null>(null)
  const swipeStartX = useRef<number | null>(null)
  const [supplierOptions, setSupplierOptions] = useState<ManufacturerSearchItem[]>([])
  const [supplierLookupStatus, setSupplierLookupStatus] = useState<'idle' | 'loading' | 'failed'>('idle')
  const [manufacturerSalesShares, setManufacturerSalesShares] = useState<ManufacturerSalesShares>(new Map())
  const [loadStatus, setLoadStatus] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [mutationPending, setMutationPending] = useState(false)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState<DestructiveConfirmation | null>(null)
  const [archiveItems, setArchiveItems] = useState<GoldExhibitionArchivedRecord[]>([])
  const [archiveStatus, setArchiveStatus] = useState<'idle' | 'loading' | 'failed'>('idle')
  const keyboardInset = useVisualKeyboardInset()

  const mutate = async (action: () => Promise<void>): Promise<void> => {
    setMutationPending(true)
    setMutationError(null)
    try {
      await action()
    } catch {
      setMutationError('Не удалось сохранить изменения. Проверьте интернет и повторите.')
    } finally {
      setMutationPending(false)
    }
  }

  useEffect(() => {
    setConfirmation(null)
  }, [location.pathname])

  useEffect(() => {
    const controller = new AbortController()
    setLoadStatus('loading')
    loadAllExhibitions(controller.signal)
      .then((exhibitions) => {
        if (controller.signal.aborted) return
        setState({ selectedExhibitionId: exhibitions[0]?.id ?? '', exhibitions })
        setLoadStatus('ready')
      })
      .catch(() => { if (!controller.signal.aborted) setLoadStatus('failed') })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    loadManufacturerSalesShares(controller.signal)
      .then((shares) => { if (!controller.signal.aborted) setManufacturerSalesShares(shares) })
      .catch(() => { if (!controller.signal.aborted) setManufacturerSalesShares(new Map()) })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (screen.kind !== 'archive') return
    const controller = new AbortController()
    setArchiveStatus('loading')
    const load = async () => {
      const items: GoldExhibitionArchivedRecord[] = []
      let cursor: string | undefined
      do {
        const response = await listGoldExhibitionArchive(cursor === undefined ? { limit: 100 } : { limit: 100, cursor }, controller.signal)
        items.push(...response.items)
        cursor = response.nextCursor ?? undefined
      } while (cursor !== undefined)
      return items
    }
    load().then((items) => {
      if (controller.signal.aborted) return
      setArchiveItems(items)
      setArchiveStatus('idle')
    }).catch(() => { if (!controller.signal.aborted) setArchiveStatus('failed') })
    return () => controller.abort()
  }, [screen.kind])

  const exhibitionId = 'exhibitionId' in screen ? screen.exhibitionId : null
  const exhibition = exhibitionId === null ? null : state.exhibitions.find((item) => item.id === exhibitionId) ?? null
  const supplierId = screen.kind === 'supplier' || screen.kind === 'new-batch' || screen.kind === 'edit-batch' || screen.kind === 'supplier-settings'
    ? screen.supplierId
    : null
  const supplier = exhibition?.suppliers.find((item) => item.id === supplierId) ?? null
  const selectedBatch = screen.kind === 'edit-batch'
    ? supplier?.batches.find((item) => item.id === screen.batchId) ?? null
    : null
  const saveBatchDraft = async (draft: { weight: string; rate: string }): Promise<boolean> => {
    if (screen.kind !== 'edit-batch' || !exhibition || !supplier || !selectedBatch) return true
    const weight = parseGramsInput(draft.weight)
    const rate = parseRublesPerGramInput(draft.rate)
    if (weight.status !== 'valid' || rate.status !== 'valid') return false
    const nextWeight = Number(weight.scaled)
    const nextRate = Number(rate.scaled)
    const persisted = batchPersistedRef.current
    if (persisted?.batchId === selectedBatch.id && persisted.weightMilliGrams === nextWeight && persisted.priceKopecksPerGram === nextRate) return true

    const pendingSave = batchSavePromiseRef.current
    if (pendingSave !== null) {
      await pendingSave
      return saveBatchDraft(draft)
    }

    let saved = false
    const request = mutate(async () => {
      const updated = batchFromApi(await updateGoldExhibitionPurchaseBatch(
        exhibition.id,
        supplier.id,
        selectedBatch.id,
        { weightMilliGrams: nextWeight, priceKopecksPerGram: nextRate },
      ))
      batchPersistedRef.current = {
        batchId: updated.id,
        weightMilliGrams: updated.weightMilliGrams,
        priceKopecksPerGram: updated.priceKopecksPerGram,
      }
      setState((current) => ({
        ...current,
        exhibitions: current.exhibitions.map((item) => item.id === exhibition.id
          ? {
              ...item,
              suppliers: item.suppliers.map((entry) => entry.id === supplier.id
                ? { ...entry, batches: entry.batches.map((batch) => batch.id === updated.id ? updated : batch) }
                : entry),
            }
          : item),
      }))
      saved = true
    })
    const pending = request.then(() => saved)
    batchSavePromiseRef.current = pending
    try {
      return await pending
    } finally {
      if (batchSavePromiseRef.current === pending) batchSavePromiseRef.current = null
    }
  }
  batchSaveRef.current = saveBatchDraft

  const saveSupplierRateDraft = async (draft: string): Promise<boolean> => {
    if (screen.kind !== 'supplier-settings' || !exhibition || !supplier || supplierSettingsRateSupplierId !== supplier.id) return true
    const rate = parseRublesPerGramInput(draft)
    if (rate.status !== 'valid') return true
    const nextRate = Number(rate.scaled)
    const persisted = supplierRatePersistedRef.current
    if (persisted?.supplierId === supplier.id && persisted.rate === nextRate) return true

    const pendingSave = supplierRateSavePromiseRef.current
    if (pendingSave !== null) {
      await pendingSave
      const latestPersisted = supplierRatePersistedRef.current
      if (latestPersisted?.supplierId === supplier.id && latestPersisted.rate === nextRate) return true
      return saveSupplierRateDraft(draft)
    }

    let saved = false
    const request = mutate(async () => {
      const updated = supplierFromApi(await updateGoldExhibitionSupplier(exhibition.id, supplier.id, { priceKopecksPerGram: nextRate }))
      supplierRatePersistedRef.current = { supplierId: updated.id, rate: updated.priceKopecksPerGram }
      setState((current) => ({
        ...current,
        exhibitions: current.exhibitions.map((item) => item.id === exhibition.id
          ? { ...item, suppliers: item.suppliers.map((entry) => entry.id === updated.id ? updated : entry) }
          : item),
      }))
      saved = true
    })
    const pending = request.then(() => saved)
    supplierRateSavePromiseRef.current = pending
    try {
      return await pending
    } finally {
      if (supplierRateSavePromiseRef.current === pending) supplierRateSavePromiseRef.current = null
    }
  }
  supplierRateSaveRef.current = saveSupplierRateDraft

  useEffect(() => {
    if (screen.kind !== 'supplier-settings' || !supplier || supplierSettingsRateSupplierId !== supplier.id) return
    const rate = parseRublesPerGramInput(supplierSettingsRate)
    const persisted = supplierRatePersistedRef.current
    const persistedRate = persisted?.supplierId === supplier.id ? persisted.rate : supplier.priceKopecksPerGram
    if (rate.status !== 'valid' || Number(rate.scaled) === persistedRate) return

    const timer = window.setTimeout(() => {
      supplierRateSaveTimerRef.current = null
      void supplierRateSaveRef.current(supplierSettingsRate)
    }, 450)
    supplierRateSaveTimerRef.current = timer
    return () => {
      window.clearTimeout(timer)
      if (supplierRateSaveTimerRef.current === timer) supplierRateSaveTimerRef.current = null
    }
  }, [screen.kind, supplier?.id, supplier?.priceKopecksPerGram, supplierSettingsRate, supplierSettingsRateSupplierId])

  const supplierSettingsBackTo = screen.kind === 'supplier-settings'
    ? prototypePath(screen.exhibitionId, 'suppliers', screen.supplierId)
    : null
  const handleSupplierSettingsBack = useCallback(() => {
    if (supplierSettingsBackTo === null) return
    void supplierRateSaveRef.current(supplierSettingsRateDraftRef.current).then((saved) => {
      if (saved) navigate(supplierSettingsBackTo)
    })
  }, [navigate, supplierSettingsBackTo])
  const batchEditorBackTo = screen.kind === 'edit-batch'
    ? prototypePath(screen.exhibitionId, 'suppliers', screen.supplierId)
    : null
  const handleBatchEditorBack = useCallback(() => {
    if (batchEditorBackTo === null) return
    if (batchSaveTimerRef.current !== null) {
      window.clearTimeout(batchSaveTimerRef.current)
      batchSaveTimerRef.current = null
    }
    void batchSaveRef.current(batchDraftRef.current).then((saved) => {
      if (saved) navigate(batchEditorBackTo)
    })
  }, [batchEditorBackTo, navigate])
  const mobileNavigation = useMemo<MobileNavigationState>(() => {
    if (screen.kind === 'home') return { title: 'Закупка', backTo: null, action: { ariaLabel: 'Архив', kind: 'archive', to: prototypePath('archive') } }
    if (screen.kind === 'archive') return { title: 'Архив', backTo: GOLD_EXHIBITION_ROOT }
    if (screen.kind === 'new-exhibition') return { title: 'Новая выставка', backTo: GOLD_EXHIBITION_ROOT }
    if (screen.kind === 'exhibition') return { title: exhibition?.name ?? 'Выставка', subtitle: exhibition ? exhibitionDateLabel(exhibition.startsOn, exhibition.endsOn) : null, backTo: GOLD_EXHIBITION_ROOT, action: { ariaLabel: 'Настройки выставки', kind: 'settings', to: prototypePath(screen.exhibitionId, 'settings') } }
    if (screen.kind === 'exhibition-settings') return { title: 'Настройки выставки', subtitle: exhibition?.name, backTo: prototypePath(screen.exhibitionId) }
    if (screen.kind === 'new-supplier') return { title: 'Новый поставщик', subtitle: exhibition?.name, backTo: prototypePath(screen.exhibitionId) }
    if (screen.kind === 'supplier') return { title: supplier?.name ?? 'Поставщик', subtitle: exhibition?.name, backTo: prototypePath(screen.exhibitionId), action: { ariaLabel: 'Настройки поставщика', kind: 'settings', to: prototypePath(screen.exhibitionId, 'suppliers', screen.supplierId, 'settings') } }
    if (screen.kind === 'supplier-settings') return { title: 'Настройки поставщика', subtitle: supplier?.name, backTo: prototypePath(screen.exhibitionId, 'suppliers', screen.supplierId), onBack: handleSupplierSettingsBack }
    if (screen.kind === 'new-batch') return { title: 'Добавить закупку', subtitle: supplier?.name, backTo: prototypePath(screen.exhibitionId, 'suppliers', screen.supplierId) }
    return { title: 'Редактировать закупку', subtitle: supplier?.name, backTo: prototypePath(screen.exhibitionId, 'suppliers', screen.supplierId), onBack: handleBatchEditorBack }
  }, [exhibition, handleBatchEditorBack, handleSupplierSettingsBack, screen, supplier])

  useLayoutEffect(() => {
    setNavigation(mobileNavigation)
    return () => setNavigation(null)
  }, [mobileNavigation, setNavigation])

  const exhibitionTotals = useMemo(() => {
    if (!exhibition) return { weight: BigInt(0), amount: BigInt(0) }
    return totalsForExhibition(exhibition)
  }, [exhibition])
  const knownSupplierNames = [...new Set(state.exhibitions.flatMap((item) => item.suppliers.map((entry) => entry.name)))]
  const supplierNameConflict = exhibition !== null && supplierName.trim().length > 0 && exhibition.suppliers.some(
    (entry) => entry.name.trim().toLocaleLowerCase('ru-RU') === supplierName.trim().toLocaleLowerCase('ru-RU'),
  )

  useEffect(() => {
    if (screen.kind !== 'exhibition-settings' || !exhibition) return
    setSettingsName(exhibition.name)
    setSettingsBudget((exhibition.budgetKopecks / 100).toFixed(2).replace('.', ','))
    setSettingsStartsOn(exhibition.startsOn ?? '')
    setSettingsEndsOn(exhibition.endsOn ?? '')
    setSettingsImageUrl(exhibition.imageUrl ?? '')
  }, [exhibition?.id, screen.kind])

  useEffect(() => {
    if (screen.kind !== 'supplier-settings' || !supplier) return
    setSupplierSettingsRate(supplier.priceKopecksPerGram === null ? '' : (supplier.priceKopecksPerGram / 100).toFixed(2).replace('.', ','))
    setSupplierSettingsRateSupplierId(supplier.id)
    supplierRatePersistedRef.current = { supplierId: supplier.id, rate: supplier.priceKopecksPerGram }
  }, [screen.kind, supplier?.id])

  useEffect(() => {
    if (screen.kind !== 'new-batch' || !supplier) return
    setBatchRate(supplier.priceKopecksPerGram === null ? '' : (supplier.priceKopecksPerGram / 100).toFixed(2).replace('.', ','))
  }, [screen.kind, supplier?.id])

  useEffect(() => {
    if (screen.kind !== 'edit-batch' || !selectedBatch) return
    setEditBatchWeight((selectedBatch.weightMilliGrams / 1000).toFixed(3).replace('.', ','))
    setEditBatchRate(selectedBatch.priceKopecksPerGram === null ? '' : (selectedBatch.priceKopecksPerGram / 100).toFixed(2).replace('.', ','))
    batchPersistedRef.current = {
      batchId: selectedBatch.id,
      weightMilliGrams: selectedBatch.weightMilliGrams,
      priceKopecksPerGram: selectedBatch.priceKopecksPerGram,
    }
  }, [screen.kind, selectedBatch?.id])

  useEffect(() => {
    if (screen.kind !== 'edit-batch' || !selectedBatch) return
    const weight = parseGramsInput(editBatchWeight)
    const rate = parseRublesPerGramInput(editBatchRate)
    const persisted = batchPersistedRef.current
    if (weight.status !== 'valid' || rate.status !== 'valid') return
    if (persisted?.batchId === selectedBatch.id && persisted.weightMilliGrams === Number(weight.scaled) && persisted.priceKopecksPerGram === Number(rate.scaled)) return

    const timer = window.setTimeout(() => {
      batchSaveTimerRef.current = null
      void batchSaveRef.current(batchDraftRef.current)
    }, 450)
    batchSaveTimerRef.current = timer
    return () => {
      window.clearTimeout(timer)
      if (batchSaveTimerRef.current === timer) batchSaveTimerRef.current = null
    }
  }, [editBatchRate, editBatchWeight, screen.kind, selectedBatch?.id])

  useEffect(() => {
    if (screen.kind !== 'new-supplier' || supplierManufacturerId !== null || supplierName.trim().length < 2) {
      setSupplierOptions([])
      setSupplierLookupStatus('idle')
      return
    }
    const controller = new AbortController()
    setSupplierLookupStatus('loading')
    searchManufacturers(supplierName.trim(), controller.signal).then((items) => {
      if (controller.signal.aborted) return
      setSupplierOptions(items)
      setSupplierLookupStatus('idle')
    }).catch(() => {
      if (controller.signal.aborted) return
      setSupplierOptions([])
      setSupplierLookupStatus('failed')
    })
    return () => controller.abort()
  }, [screen.kind, supplierManufacturerId, supplierName])

  const createSupplier = async () => {
    const rate = parseRublesPerGramInput(supplierRate)
    const name = supplierName.trim()
    if (!exhibition || !name || rate.status !== 'valid' || supplierNameConflict) return
    await mutate(async () => {
      const nextSupplier = await createGoldExhibitionSupplier(exhibition.id, { name, manufacturerId: supplierManufacturerId, priceKopecksPerGram: Number(rate.scaled) })
      setState((current) => ({
        ...current,
        exhibitions: current.exhibitions.map((item) => item.id === exhibition.id
          ? { ...item, suppliers: [...item.suppliers, supplierFromApi(nextSupplier)] }
          : item),
      }))
      setSupplierName('')
      setSupplierManufacturerId(null)
      setSupplierRate('')
      navigate(prototypePath(exhibition.id, 'suppliers', nextSupplier.id))
    })
  }

  const createExhibition = async () => {
    const budgetKopecks = parseBudget(exhibitionBudget)
    const name = exhibitionName.trim()
    if (!name || budgetKopecks === null) return
    await mutate(async () => {
      const nextExhibition = await createGoldExhibition({
        name,
        budgetKopecks,
        startsOn: exhibitionStartsOn || null,
        endsOn: exhibitionEndsOn || null,
        imageUrl: exhibitionImageUrl.trim() || null,
      })
      const created = fromApi(nextExhibition)
      setState((current) => ({ selectedExhibitionId: created.id, exhibitions: [created, ...current.exhibitions] }))
      setExhibitionName('')
      setExhibitionBudget('')
      setExhibitionStartsOn('')
      setExhibitionEndsOn('')
      setExhibitionImageUrl('')
      navigate(prototypePath(nextExhibition.id))
    })
  }

  const addBatch = async () => {
    const weight = parseGramsInput(batchWeight)
    const rate = parseRublesPerGramInput(batchRate)
    if (!exhibition || !supplier || weight.status !== 'valid' || rate.status !== 'valid') return
    await mutate(async () => {
      const created = await createGoldExhibitionPurchaseBatch(exhibition.id, supplier.id, {
        weightMilliGrams: Number(weight.scaled),
        priceKopecksPerGram: Number(rate.scaled),
      })
      setState((current) => ({
        ...current,
        exhibitions: current.exhibitions.map((item) => item.id === exhibition.id
          ? {
              ...item,
              suppliers: item.suppliers.map((entry) => entry.id === supplier.id
                ? { ...entry, batches: [batchFromApi(created), ...entry.batches] }
                : entry),
            }
          : item),
      }))
      setBatchWeight('')
      setBatchRate('')
      navigate(prototypePath(exhibition.id, 'suppliers', supplier.id))
    })
  }

  const deleteBatch = (batch: PurchaseBatch) => {
    if (!exhibition || !supplier) return
    setConfirmation({ kind: 'delete-batch', exhibitionId: exhibition.id, supplierId: supplier.id, supplierName: supplier.name, batch })
  }

  const archiveExhibition = () => {
    if (!exhibition) return
    setConfirmation({ kind: 'archive-exhibition', exhibitionId: exhibition.id, exhibitionName: exhibition.name })
  }

  const archiveSupplier = () => {
    if (!exhibition || !supplier) return
    setConfirmation({ kind: 'archive-supplier', exhibitionId: exhibition.id, supplierId: supplier.id, supplierName: supplier.name })
  }

  const confirmDestructiveAction = async () => {
    if (confirmation === null) return
    const action = confirmation
    await mutate(async () => {
      if (action.kind === 'delete-batch') {
        await deleteGoldExhibitionPurchaseBatch(action.exhibitionId, action.supplierId, action.batch.id)
        setState((current) => ({
          ...current,
          exhibitions: current.exhibitions.map((item) => item.id === action.exhibitionId
            ? {
                ...item,
                suppliers: item.suppliers.map((entry) => entry.id === action.supplierId
                  ? { ...entry, batches: entry.batches.filter((candidate) => candidate.id !== action.batch.id) }
                  : entry),
              }
            : item),
        }))
        setSwipedBatchId(null)
      } else if (action.kind === 'archive-exhibition') {
        await archiveGoldExhibition(action.exhibitionId)
        setState((current) => ({ ...current, exhibitions: current.exhibitions.filter((item) => item.id !== action.exhibitionId) }))
      } else {
        await archiveGoldExhibitionSupplier(action.exhibitionId, action.supplierId)
        setState((current) => ({
          ...current,
          exhibitions: current.exhibitions.map((item) => item.id === action.exhibitionId
            ? { ...item, suppliers: item.suppliers.filter((entry) => entry.id !== action.supplierId) }
            : item),
        }))
      }
      setConfirmation(null)
      if (action.kind === 'archive-exhibition') navigate(GOLD_EXHIBITION_ROOT)
      if (action.kind === 'archive-supplier') navigate(prototypePath(action.exhibitionId))
    })
  }

  const restoreArchivedItem = async (item: GoldExhibitionArchivedRecord) => {
    await mutate(async () => {
      if (item.kind === 'exhibition') {
        await restoreGoldExhibition(item.exhibitionId)
      } else {
        await restoreGoldExhibitionSupplier(item.exhibitionId, item.id)
      }
      const exhibitions = await loadAllExhibitions()
      setState({ selectedExhibitionId: exhibitions[0]?.id ?? '', exhibitions })
      setArchiveItems((current) => current.filter((candidate) => candidate.kind !== item.kind || candidate.id !== item.id))
    })
  }

  if (screen.kind === 'home') {
    const groups = groupedExhibitions(state.exhibitions, localCalendarDate())
    const sections: Array<{ items: PrototypeExhibition[]; key: ExhibitionTiming; title: string }> = [
      { key: 'current', title: 'Текущая выставка', items: groups.current },
      { key: 'future', title: 'Будущие выставки', items: groups.future },
      { key: 'past', title: 'Прошлые выставки', items: groups.past },
      { key: 'undated', title: 'Без дат', items: groups.undated },
    ]
    return <section className="mx-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-ivory" aria-label="Закупка">
      <header className="hidden min-h-16 shrink-0 items-center justify-between gap-3 border-b border-rule bg-paper px-4 md:flex">
        <h1 className="text-[22px] font-semibold tracking-tight">Закупка</h1>
        <button type="button" aria-label="Архив" title="Архив" onClick={() => navigate(prototypePath('archive'))} className="grid size-11 shrink-0 place-items-center rounded-full border border-rule bg-paper text-ink-muted active:bg-bone"><ArchiveIcon /></button>
      </header>
      <div className="flex-1 overflow-y-auto px-4 pb-28 pt-4">
        {loadStatus === 'loading' && <p role="status" className="py-8 text-center text-sm text-ink-muted">Загружаем выставки…</p>}
        {loadStatus === 'failed' && <p role="alert" className="py-8 text-center text-sm text-st-unpaid">Не удалось загрузить общие выставки.</p>}
        <div className="space-y-6">
          {sections.filter((section) => section.items.length > 0).map((section) => <section key={section.key} aria-labelledby={`exhibition-section-${section.key}`}>
            <div className="mb-2.5 flex items-center gap-2 px-0.5">
              {section.key === 'current' && <span className="size-2.5 rounded-full bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.12)]" />}
              <h2 id={`exhibition-section-${section.key}`} className="text-[16px] font-semibold text-ink">{section.title}</h2>
            </div>
            <ul className="space-y-2">
              {section.items.map((item) => {
                const totals = totalsForExhibition(item)
                const remaining = BigInt(item.budgetKopecks) - totals.amount
                const current = section.key === 'current'
                return <li key={item.id}>
                  <button type="button" onClick={() => navigate(prototypePath(item.id))} className={`w-full overflow-hidden rounded-2xl border bg-paper text-left active:scale-[0.995] ${current ? 'border-emerald-300 shadow-[0_5px_18px_rgba(16,185,129,0.13)] ring-1 ring-emerald-100' : 'border-rule shadow-[0_2px_8px_rgba(20,17,13,0.08)]'}`}>
                    <span className="relative flex h-28 items-end overflow-hidden bg-gradient-to-br from-amber-950 via-stone-800 to-amber-700 px-4 py-3 text-white">
                      {item.imageUrl && <img src={item.imageUrl} alt="" className="absolute inset-0 size-full scale-110 object-cover blur-[10px] saturate-75" />}
                      <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
                      {current && <span className="absolute left-3 top-3 rounded-full bg-emerald-500/90 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur">Идёт сейчас</span>}
                      <span className="relative flex min-w-0 flex-1 items-end justify-between gap-3"><span className="min-w-0"><strong className="block truncate text-[19px] font-semibold">{item.name}</strong><span className="mt-1 block text-[11px] text-white/75">{exhibitionDateLabel(item.startsOn, item.endsOn)} · {item.suppliers.length} поставщиков</span></span><span aria-hidden="true" className="pb-0.5 text-2xl leading-none text-white/80">›</span></span>
                    </span>
                    <span className="grid grid-cols-2 gap-3 px-4 pb-3 pt-3 tabular-nums">
                      <span><small className="block text-[10px] text-ink-faint">Купили</small><span className="text-base font-semibold">{formatMilliGrams(totals.weight)}</span></span>
                      <span className="text-right"><small className="block text-[10px] text-ink-faint">На сумму</small><span className="text-base font-semibold">{formatKopecks(totals.amount)}</span></span>
                    </span>
                    <span className={`block border-t border-rule px-4 py-2.5 text-[11px] tabular-nums ${item.budgetKopecks > 0 && remaining < 0 ? 'text-st-unpaid' : 'text-ink-muted'}`}>{item.budgetKopecks === 0 ? 'Бюджет не задан' : remaining < 0 ? `Превышен на ${formatKopecks(-remaining)}` : `Осталось ${formatKopecks(remaining)}`}</span>
                  </button>
                </li>
              })}
            </ul>
          </section>)}
        </div>
      </div>
      <BottomAction label="Новая выставка" keyboardInset={keyboardInset} onClick={() => navigate(prototypePath('new'))} />
    </section>
  }

  if (screen.kind === 'archive') {
    return <section className="mx-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-ivory" aria-label="Архив закупок">
      <header className="hidden min-h-16 shrink-0 items-center gap-2 border-b border-rule bg-paper px-2 md:flex"><BackButton onClick={() => navigate(GOLD_EXHIBITION_ROOT)} /><h1 className="text-[20px] font-semibold">Архив</h1></header>
      <div className="flex-1 overflow-y-auto px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-4">
        <p className="mb-4 text-sm leading-5 text-ink-muted">Удалённые выставки и поставщики сохраняются здесь. Их можно восстановить вместе с историей закупок.</p>
        {archiveStatus === 'loading' && <p role="status" className="py-8 text-center text-sm text-ink-muted">Загружаем архив…</p>}
        {archiveStatus === 'failed' && <p role="alert" className="py-8 text-center text-sm text-st-unpaid">Не удалось загрузить архив.</p>}
        {archiveStatus === 'idle' && archiveItems.length === 0 && <div className="rounded-xl border border-dashed border-rule-strong px-5 py-12 text-center text-sm text-ink-muted">Архив пуст</div>}
        <ol className="space-y-2">
          {archiveItems.map((item) => <li key={`${item.kind}:${item.id}`} className="rounded-xl border border-rule bg-paper p-4 shadow-[0_1px_2px_rgba(20,17,13,0.04)]">
            <div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="text-[10px] font-medium uppercase tracking-wide text-ink-faint">{item.kind === 'exhibition' ? 'Выставка' : 'Поставщик'}</p><h2 className="mt-1 truncate text-[16px] font-semibold">{item.name}</h2>{item.kind === 'supplier' && <p className="mt-0.5 truncate text-xs text-ink-muted">{item.exhibitionName}</p>}<p className="mt-2 text-[11px] text-ink-faint">Архивировано {new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(item.archivedAt))}</p></div><button type="button" disabled={mutationPending} onClick={() => void restoreArchivedItem(item)} className="min-h-10 shrink-0 rounded-lg border border-rule-strong px-3 text-sm font-semibold text-ink-2 active:bg-bone disabled:opacity-40">Восстановить</button></div>
          </li>)}
        </ol>
        <MutationAlert message={mutationError} />
      </div>
    </section>
  }

  if (screen.kind === 'new-exhibition') {
    const budget = parseBudget(exhibitionBudget)
    const datesValid = !exhibitionStartsOn || !exhibitionEndsOn || exhibitionStartsOn <= exhibitionEndsOn
    const imageValid = !exhibitionImageUrl.trim() || exhibitionImageUrl.trim().startsWith('https://')
    return <section className="mx-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-ivory" aria-label="Новая выставка">
      <header className="hidden min-h-14 shrink-0 items-center gap-2 border-b border-rule bg-paper px-2 md:flex">
        <BackButton onClick={() => navigate(GOLD_EXHIBITION_ROOT)} />
        <h1 className="text-[18px] font-semibold">Новая выставка</h1>
      </header>
      <div className="flex-1 overflow-y-auto px-4 pb-28 pt-5">
        <label htmlFor="prototype-exhibition-name" className="mb-1.5 block text-xs font-medium text-ink-2">Название выставки</label>
        <input id="prototype-exhibition-name" autoFocus value={exhibitionName} onChange={(event) => setExhibitionName(event.currentTarget.value)} className="min-h-12 w-full rounded-xl border border-rule-strong bg-paper px-4 text-[16px] outline-none focus:border-gold-deep focus:ring-2 focus:ring-gold/25" placeholder="Например, Junwex Москва" />
        {!exhibitionName.trim() && <p className="mt-1.5 text-xs leading-4 text-ink-faint">Укажите название выставки.</p>}
        <label htmlFor="prototype-exhibition-budget" className="mb-1.5 mt-5 block text-xs font-medium text-ink-2">Бюджет выставки, ₽</label>
        <input id="prototype-exhibition-budget" inputMode="decimal" value={exhibitionBudget} onChange={(event) => setExhibitionBudget(event.currentTarget.value)} className="min-h-14 w-full rounded-xl border border-rule-strong bg-paper px-4 text-[22px] font-semibold tabular-nums outline-none focus:border-gold-deep focus:ring-2 focus:ring-gold/25" placeholder="0,00" />
        {!exhibitionBudget.trim() && <p className="mt-1.5 text-xs leading-4 text-ink-faint">Укажите бюджет выставки.</p>}
        {exhibitionBudget && budget === null && <p className="mt-2 text-sm text-st-unpaid">Введите бюджет цифрами.</p>}
        <div className="mt-5 grid grid-cols-2 gap-3"><label className="text-xs font-medium text-ink-2">Начало<input aria-label="Дата начала" type="date" value={exhibitionStartsOn} onChange={(event) => setExhibitionStartsOn(event.currentTarget.value)} className="mt-1.5 min-h-12 w-full rounded-xl border border-rule-strong bg-paper px-3 text-[16px] outline-none focus:border-gold-deep" /></label><label className="text-xs font-medium text-ink-2">Окончание<input aria-label="Дата окончания" type="date" value={exhibitionEndsOn} onChange={(event) => setExhibitionEndsOn(event.currentTarget.value)} className="mt-1.5 min-h-12 w-full rounded-xl border border-rule-strong bg-paper px-3 text-[16px] outline-none focus:border-gold-deep" /></label></div>
        {!datesValid && <p className="mt-2 text-sm text-st-unpaid">Дата окончания не может быть раньше даты начала.</p>}
        <label htmlFor="prototype-exhibition-image" className="mb-1.5 mt-5 block text-xs font-medium text-ink-2">Обложка, HTTPS-ссылка</label>
        <input id="prototype-exhibition-image" type="url" inputMode="url" value={exhibitionImageUrl} onChange={(event) => setExhibitionImageUrl(event.currentTarget.value)} className="min-h-12 w-full rounded-xl border border-rule-strong bg-paper px-4 text-[16px] outline-none focus:border-gold-deep" placeholder="https://…" />
        {!imageValid && <p className="mt-2 text-sm text-st-unpaid">Укажите HTTPS-ссылку на изображение.</p>}
      </div>
      <MutationAlert message={mutationError} />
      <BottomAction label={mutationPending ? 'Создаём…' : 'Создать выставку'} disabled={!exhibitionName.trim() || budget === null || !datesValid || !imageValid || mutationPending} keyboardInset={keyboardInset} onClick={() => void createExhibition()} />
    </section>
  }

  if (!exhibition && loadStatus === 'loading') {
    return <section className="mx-auto grid h-full w-full max-w-xl place-items-center bg-ivory px-6 text-center" aria-busy="true"><p className="text-sm text-ink-muted">Загружаем выставку…</p></section>
  }

  if (!exhibition) {
    return <section className="mx-auto grid h-full w-full max-w-xl place-items-center bg-ivory px-6 text-center"><div><h1 className="text-lg font-semibold">Выставка не найдена</h1><button type="button" onClick={() => navigate(GOLD_EXHIBITION_ROOT, { replace: true })} className="mt-4 min-h-11 rounded-xl bg-onyx px-5 text-sm font-semibold text-white">К списку выставок</button></div></section>
  }

  if (screen.kind === 'exhibition-settings') {
    const budget = parseBudget(settingsBudget)
    const datesValid = !settingsStartsOn || !settingsEndsOn || settingsStartsOn <= settingsEndsOn
    const imageValid = !settingsImageUrl.trim() || settingsImageUrl.trim().startsWith('https://')
    const saveSettings = async () => {
      const name = settingsName.trim()
      const imageUrl = settingsImageUrl.trim() || null
      const startsOn = settingsStartsOn || null
      const endsOn = settingsEndsOn || null
      if (!name || budget === null || !datesValid || !imageValid) return
      if (name === exhibition.name && budget === exhibition.budgetKopecks && startsOn === exhibition.startsOn && endsOn === exhibition.endsOn && imageUrl === exhibition.imageUrl) return
      await mutate(async () => {
        const updated = fromApi(await updateGoldExhibition(exhibition.id, {
          name,
          budgetKopecks: budget,
          startsOn,
          endsOn,
          imageUrl,
        }))
        setState((current) => ({ ...current, exhibitions: current.exhibitions.map((item) => item.id === updated.id ? updated : item) }))
      })
    }
    return <section className="mx-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-ivory" aria-label="Настройки выставки">
      <header className="hidden min-h-14 shrink-0 items-center gap-2 border-b border-rule bg-paper px-2 md:flex">
        <BackButton onClick={() => navigate(prototypePath(exhibition.id))} />
        <div className="min-w-0"><p className="truncate text-[11px] text-ink-faint">{exhibition.name}</p><h1 className="text-[18px] font-semibold">Настройки</h1></div>
      </header>
      <div className="flex-1 overflow-y-auto px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-5">
        <label htmlFor="prototype-settings-name" className="mb-1.5 block text-xs font-medium text-ink-2">Название выставки</label>
        <input id="prototype-settings-name" autoFocus value={settingsName} onBlur={() => void saveSettings()} onChange={(event) => setSettingsName(event.currentTarget.value)} className="min-h-12 w-full rounded-xl border border-rule-strong bg-paper px-4 text-[16px] outline-none focus:border-gold-deep focus:ring-2 focus:ring-gold/25" />
        <label htmlFor="prototype-settings-budget" className="mb-1.5 mt-5 block text-xs font-medium text-ink-2">Бюджет выставки, ₽</label>
        <input id="prototype-settings-budget" inputMode="decimal" value={settingsBudget} onBlur={() => void saveSettings()} onChange={(event) => setSettingsBudget(event.currentTarget.value)} className="min-h-14 w-full rounded-xl border border-rule-strong bg-paper px-4 text-[22px] font-semibold tabular-nums outline-none focus:border-gold-deep focus:ring-2 focus:ring-gold/25" />
        {settingsBudget && budget === null && <p className="mt-2 text-sm text-st-unpaid">Введите бюджет цифрами.</p>}
        <div className="mt-5 grid grid-cols-2 gap-3"><label className="text-xs font-medium text-ink-2">Начало<input aria-label="Дата начала" type="date" value={settingsStartsOn} onBlur={() => void saveSettings()} onChange={(event) => setSettingsStartsOn(event.currentTarget.value)} className="mt-1.5 min-h-12 w-full rounded-xl border border-rule-strong bg-paper px-3 text-[16px] outline-none focus:border-gold-deep" /></label><label className="text-xs font-medium text-ink-2">Окончание<input aria-label="Дата окончания" type="date" value={settingsEndsOn} onBlur={() => void saveSettings()} onChange={(event) => setSettingsEndsOn(event.currentTarget.value)} className="mt-1.5 min-h-12 w-full rounded-xl border border-rule-strong bg-paper px-3 text-[16px] outline-none focus:border-gold-deep" /></label></div>
        {!datesValid && <p className="mt-2 text-sm text-st-unpaid">Дата окончания не может быть раньше даты начала.</p>}
        <label htmlFor="prototype-settings-image" className="mb-1.5 mt-5 block text-xs font-medium text-ink-2">Обложка, HTTPS-ссылка</label>
        <input id="prototype-settings-image" type="url" inputMode="url" value={settingsImageUrl} onBlur={() => void saveSettings()} onChange={(event) => setSettingsImageUrl(event.currentTarget.value)} className="min-h-12 w-full rounded-xl border border-rule-strong bg-paper px-4 text-[16px] outline-none focus:border-gold-deep" placeholder="https://…" />
        {!imageValid && <p className="mt-2 text-sm text-st-unpaid">Укажите HTTPS-ссылку на изображение.</p>}
        <AutoSaveHint
          pending={mutationPending}
          valid={Boolean(settingsName.trim()) && budget !== null && datesValid && imageValid}
          saved={settingsName === exhibition.name && budget === exhibition.budgetKopecks && (settingsStartsOn || null) === exhibition.startsOn && (settingsEndsOn || null) === exhibition.endsOn && (settingsImageUrl.trim() || null) === exhibition.imageUrl}
          error={mutationError}
        />
        <div className="mt-10 border-t border-rule pt-5"><button type="button" disabled={mutationPending} onClick={() => void archiveExhibition()} className="min-h-12 w-full rounded-xl border border-red-300 bg-red-50 px-4 text-sm font-semibold text-st-unpaid active:bg-red-100 disabled:opacity-40">Удалить выставку</button><p className="mt-2 text-xs leading-4 text-ink-faint">Выставка попадёт в архив и останется доступна для восстановления.</p></div>
      </div>
      <MutationAlert message={mutationError} />
      <DestructiveConfirmationDialog action={confirmation?.kind === 'archive-exhibition' ? confirmation : null} busy={mutationPending} error={mutationError} onCancel={() => setConfirmation(null)} onConfirm={() => void confirmDestructiveAction()} />
    </section>
  }

  if (screen.kind === 'supplier-settings' && supplier) {
    const rate = parseRublesPerGramInput(supplierSettingsRate)
    const flushRateSave = () => {
      if (supplierRateSaveTimerRef.current !== null) {
        window.clearTimeout(supplierRateSaveTimerRef.current)
        supplierRateSaveTimerRef.current = null
      }
      void saveSupplierRateDraft(supplierSettingsRate)
    }
    return <section className="mx-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-ivory" aria-label="Настройки поставщика">
      <header className="hidden min-h-14 shrink-0 items-center gap-2 border-b border-rule bg-paper px-2 md:flex"><BackButton onClick={handleSupplierSettingsBack} /><div className="min-w-0"><p className="truncate text-[11px] text-ink-faint">{supplier.name}</p><h1 className="text-[18px] font-semibold">Настройки поставщика</h1></div></header>
      <div className="flex-1 overflow-y-auto px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-5"><label htmlFor="prototype-supplier-settings-rate" className="mb-1.5 block text-xs font-medium text-ink-2">Закупочная цена, ₽ за грамм</label><input id="prototype-supplier-settings-rate" autoFocus inputMode="decimal" value={supplierSettingsRate} onBlur={flushRateSave} onChange={(event) => setSupplierSettingsRate(event.currentTarget.value)} className="min-h-14 w-full rounded-xl border border-rule-strong bg-paper px-4 text-[22px] font-semibold tabular-nums outline-none focus:border-gold-deep focus:ring-2 focus:ring-gold/25" />{supplierSettingsRate && rate.status === 'invalid' && <p className="mt-2 text-sm text-st-unpaid">Введите цену цифрами.</p>}<AutoSaveHint pending={mutationPending} valid={rate.status === 'valid'} saved={rate.status === 'valid' && Number(rate.scaled) === supplier.priceKopecksPerGram} error={mutationError} /><div className="mt-10 border-t border-rule pt-5"><button type="button" disabled={mutationPending} onClick={() => void archiveSupplier()} className="min-h-12 w-full rounded-xl border border-red-300 bg-red-50 px-4 text-sm font-semibold text-st-unpaid active:bg-red-100 disabled:opacity-40">Удалить поставщика</button><p className="mt-2 text-xs leading-4 text-ink-faint">Поставщик и его закупки попадут в архив и останутся доступными для восстановления.</p></div></div>
      <MutationAlert message={mutationError} />
      <DestructiveConfirmationDialog action={confirmation?.kind === 'archive-supplier' ? confirmation : null} busy={mutationPending} error={mutationError} onCancel={() => setConfirmation(null)} onConfirm={() => void confirmDestructiveAction()} />
    </section>
  }

  if (screen.kind === 'new-supplier') {
    const rate = parseRublesPerGramInput(supplierRate)
    return <section className="mx-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-ivory" aria-label="Новый поставщик">
      <header className="hidden min-h-14 shrink-0 items-center gap-2 border-b border-rule bg-paper px-2 md:flex">
        <BackButton onClick={() => navigate(prototypePath(exhibition.id))} />
        <div><p className="text-[11px] text-ink-faint">{exhibition.name}</p><h1 className="text-[18px] font-semibold">Новый поставщик</h1></div>
      </header>
      <div className="flex-1 overflow-y-auto px-4 pb-28 pt-5">
        <p className="mb-6 text-sm leading-5 text-ink-muted">Создайте поставщика один раз. Цена за грамм сохранится для всех его закупок на этой выставке.</p>
        <label htmlFor="prototype-supplier-name" className="mb-1.5 block text-xs font-medium text-ink-2">Поставщик / продавец</label>
        <div className="relative">
          <input ref={supplierNameInputRef} id="prototype-supplier-name" list="prototype-supplier-history" autoFocus autoComplete="off" enterKeyHint="next" value={supplierName} onChange={(event) => { setSupplierName(event.currentTarget.value); setSupplierManufacturerId(null) }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); supplierRateInputRef.current?.focus({ preventScroll: true }) } }} className="min-h-12 w-full rounded-xl border border-rule-strong bg-paper px-4 text-[16px] outline-none focus:border-gold-deep focus:ring-2 focus:ring-gold/25" placeholder="Начните вводить название" aria-autocomplete="list" aria-expanded={supplierOptions.length > 0} aria-controls="prototype-supplier-options" />
          <datalist id="prototype-supplier-history">{knownSupplierNames.map((name) => <option key={name} value={name} />)}</datalist>
          {supplierOptions.length > 0 && <ul id="prototype-supplier-options" role="listbox" aria-label="Поставщики из справочника" className="absolute inset-x-0 top-[calc(100%+0.25rem)] z-20 max-h-52 overflow-y-auto rounded-xl border border-rule-strong bg-paper p-1 shadow-xl">
            {supplierOptions.map((option) => { const share = manufacturerSalesShares.get(option.analyticsKey); return <li key={option.id}><button type="button" role="option" aria-selected={supplierManufacturerId === option.id} onMouseDown={(event) => event.preventDefault()} onClick={() => { setSupplierName(option.displayName); setSupplierManufacturerId(option.id); setSupplierOptions([]); supplierRateInputRef.current?.focus({ preventScroll: true }) }} className="min-h-11 w-full rounded-lg px-3 py-2 text-left active:bg-bone"><span className="block text-[15px]">{option.displayName}</span>{share !== undefined && <span className="mt-0.5 block text-[12px] text-ink-muted">Доля продаж за 5 мес. — {formatManufacturerSalesShare(share)}</span>}</button></li> })}
          </ul>}
        </div>
        {supplierNameConflict && <p role="alert" className="mt-2 text-sm font-medium text-st-unpaid">Поставщик с таким именем уже добавлен на эту выставку.</p>}
        <p className="mt-1.5 text-xs leading-4 text-ink-faint">{supplierLookupStatus === 'loading' ? 'Ищем в справочнике…' : supplierLookupStatus === 'failed' ? 'Справочник недоступен — можно оставить имя продавца вручную.' : supplierManufacturerId ? 'Выбрано из справочника производителей.' : 'Выберите производителя из справочника или оставьте отдельное имя продавца.'}</p>
        <label htmlFor="prototype-supplier-rate" className="mb-1.5 mt-5 block text-xs font-medium text-ink-2">Закупочная цена, ₽ за грамм</label>
        <input ref={supplierRateInputRef} id="prototype-supplier-rate" inputMode="decimal" enterKeyHint="done" value={supplierRate} onChange={(event) => setSupplierRate(event.currentTarget.value)} className="min-h-14 w-full rounded-xl border border-rule-strong bg-paper px-4 text-[22px] font-semibold tabular-nums outline-none focus:border-gold-deep focus:ring-2 focus:ring-gold/25" placeholder="0,00" />
        {supplierRate && rate.status === 'invalid' && <p className="mt-2 text-sm text-st-unpaid">Введите цену цифрами, например 7 420,00.</p>}
      </div>
      <MutationAlert message={mutationError} />
      <BottomAction label={mutationPending ? 'Создаём…' : 'Создать поставщика'} disabled={!supplierName.trim() || rate.status !== 'valid' || supplierNameConflict || mutationPending} keyboardInset={keyboardInset} onClick={() => void createSupplier()} />
    </section>
  }

  if (screen.kind === 'new-batch' && supplier) {
    const weight = parseGramsInput(batchWeight)
    const rate = parseRublesPerGramInput(batchRate)
    const amount = weight.status === 'valid' && rate.status === 'valid' ? amountFor(weight.scaled, Number(rate.scaled)) : null
    const budget = BigInt(exhibition.budgetKopecks)
    const supplierIndex = Math.max(0, exhibition.suppliers.findIndex((item) => item.id === supplier.id))
    const supplierColor = SUPPLIER_COLORS[supplierIndex % SUPPLIER_COLORS.length]
    return <section className="mx-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-ivory" aria-label="Новая закупка">
      <header className="hidden min-h-14 shrink-0 items-center gap-2 border-b border-rule bg-paper px-2 md:flex">
        <BackButton onClick={() => navigate(prototypePath(exhibition.id, 'suppliers', supplier.id))} />
        <div className="min-w-0"><p className="truncate text-[11px] text-ink-faint">{supplier.name}</p><h1 className="text-[18px] font-semibold">Добавить закупку</h1></div>
      </header>
      <div className="flex-1 overflow-y-auto px-4 pb-28 pt-5">
        <PurchaseBatchEditorFields amount={amount} baselineSpent={exhibitionTotals.amount} budget={budget} inputId="prototype-batch-weight" onChange={setBatchWeight} onRateChange={setBatchRate} rateValue={batchRate} supplierColor={supplierColor} value={batchWeight} />
        {batchRate && rate.status === 'invalid' && <p className="mt-2 text-sm text-st-unpaid">Введите цену цифрами.</p>}
        {supplier.priceKopecksPerGram === null && !batchRate && <div role="alert" className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-st-unpaid"><p>Не указана цена закупки за грамм.</p><button type="button" onClick={() => navigate(prototypePath(exhibition.id, 'suppliers', supplier.id, 'settings'))} className="mt-2 min-h-10 rounded-lg bg-onyx px-4 text-sm font-semibold text-white">Перейти в настройки</button></div>}
      </div>
      <MutationAlert message={mutationError} />
      <BottomAction label={mutationPending ? 'Добавляем…' : 'Добавить закупку'} disabled={weight.status !== 'valid' || rate.status !== 'valid' || mutationPending} keyboardInset={keyboardInset} onClick={() => void addBatch()} />
    </section>
  }

  if (screen.kind === 'edit-batch' && supplier && selectedBatch) {
    const weight = parseGramsInput(editBatchWeight)
    const rate = parseRublesPerGramInput(editBatchRate)
    const amount = weight.status === 'valid' && rate.status === 'valid' ? amountFor(weight.scaled, Number(rate.scaled)) : null
    const currentAmount = amountFor(BigInt(selectedBatch.weightMilliGrams), selectedBatch.priceKopecksPerGram) ?? BigInt(0)
    const supplierIndex = Math.max(0, exhibition.suppliers.findIndex((item) => item.id === supplier.id))
    const supplierColor = SUPPLIER_COLORS[supplierIndex % SUPPLIER_COLORS.length]
    return <section className="mx-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-ivory" aria-label="Редактирование закупки">
      <header className="hidden min-h-14 shrink-0 items-center gap-2 border-b border-rule bg-paper px-2 md:flex">
        <BackButton onClick={handleBatchEditorBack} />
        <div className="min-w-0"><p className="truncate text-[11px] text-ink-faint">{supplier.name}</p><h1 className="text-[18px] font-semibold">Редактировать закупку</h1></div>
      </header>
      <div className="flex-1 overflow-y-auto px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-5">
        <PurchaseBatchEditorFields amount={amount} baselineSpent={exhibitionTotals.amount - currentAmount} budget={BigInt(exhibition.budgetKopecks)} inputId="prototype-edit-batch-weight" onBlur={() => void batchSaveRef.current(batchDraftRef.current)} onChange={setEditBatchWeight} onRateBlur={() => void batchSaveRef.current(batchDraftRef.current)} onRateChange={setEditBatchRate} rateValue={editBatchRate} supplierColor={supplierColor} value={editBatchWeight} />
        {editBatchRate && rate.status === 'invalid' && <p className="mt-2 text-sm text-st-unpaid">Введите цену цифрами.</p>}
        <AutoSaveHint
          pending={mutationPending}
          valid={weight.status === 'valid' && rate.status === 'valid'}
          saved={weight.status === 'valid' && rate.status === 'valid' && Number(weight.scaled) === selectedBatch.weightMilliGrams && Number(rate.scaled) === selectedBatch.priceKopecksPerGram}
          error={mutationError}
        />
      </div>
      <MutationAlert message={mutationError} />
    </section>
  }

  if (screen.kind === 'supplier' && supplier) {
    const weight = supplierWeight(supplier)
    const amount = supplierAmount(supplier)
    const exhibitionBudget = BigInt(exhibition.budgetKopecks)
    const otherSupplierAmount = exhibitionTotals.amount - amount
    const supplierBudgetWidth = exhibitionBudget > 0 ? Math.min(100, Number((amount * BigInt(10000)) / exhibitionBudget) / 100) : 0
    const otherBudgetWidth = exhibitionBudget > 0 ? Math.min(100 - supplierBudgetWidth, Number((otherSupplierAmount * BigInt(10000)) / exhibitionBudget) / 100) : 0
    const exhibitionOverflowWidth = exhibitionBudget > 0 && exhibitionTotals.amount > exhibitionBudget
      ? Math.min(100, Number(((exhibitionTotals.amount - exhibitionBudget) * BigInt(10000)) / exhibitionBudget) / 100)
      : 0
    const supplierIndex = Math.max(0, exhibition.suppliers.findIndex((item) => item.id === supplier.id))
    const supplierColor = SUPPLIER_COLORS[supplierIndex % SUPPLIER_COLORS.length]
    return <section className="mx-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-ivory" aria-label={`Поставщик ${supplier.name}`}>
      <header className="shrink-0 border-b border-rule bg-paper px-2 pb-4 pt-3 md:pt-0">
        <div className="hidden min-h-14 items-center gap-2 md:flex"><BackButton onClick={() => navigate(prototypePath(exhibition.id))} /><div className="min-w-0 flex-1"><p className="truncate text-[13px] text-ink-muted">{exhibition.name}</p><h1 className="flex items-center gap-2 truncate text-[24px] font-semibold"><span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: supplierColor }} />{supplier.name}</h1></div><button type="button" aria-label="Настройки поставщика" title="Настройки поставщика" onClick={() => navigate(prototypePath(exhibition.id, 'suppliers', supplier.id, 'settings'))} className="grid size-11 shrink-0 place-items-center rounded-full border border-rule bg-paper text-ink-muted active:bg-bone"><SettingsIcon /></button></div>
        <div className="mx-2 rounded-xl bg-bone/60 px-4 py-3.5">
          <div className="flex items-start justify-between gap-3">
            <div><p className="text-[12px] font-medium text-ink-muted">Сумма закупок у поставщика</p><p className="mt-0.5 whitespace-nowrap text-[20px] font-semibold tabular-nums text-ink">{formatKopecks(amount)}</p></div>
            <span className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-paper px-2.5 py-1 text-[13px] tabular-nums ${supplier.priceKopecksPerGram === null ? 'font-semibold text-st-unpaid' : 'text-ink-2'}`}><span aria-hidden="true" className="size-2 rounded-full" style={{ backgroundColor: supplierColor }} />{supplier.priceKopecksPerGram === null ? 'Цена не указана' : supplierRateLabel(supplier.priceKopecksPerGram)}</span>
          </div>
          {exhibitionBudget > 0 && <div className="relative mt-3 flex h-2 overflow-hidden rounded-full bg-black/[0.07]" role="img" aria-label={`Доля ${supplier.name} в бюджете выставки`}><span className="h-full shrink-0" style={{ width: `${supplierBudgetWidth}%`, backgroundColor: supplierColor }} /><span className="h-full shrink-0 bg-ink/25" style={{ width: `${otherBudgetWidth}%` }} />{exhibitionOverflowWidth > 0 && <span className="absolute inset-y-0 right-0 bg-red-500/85" style={{ width: `${exhibitionOverflowWidth}%` }} />}</div>}
          <div className="mt-2 flex items-center justify-between gap-3 text-[12px] text-ink-muted"><span>Доля бюджета выставки</span><span className="whitespace-nowrap tabular-nums">{formatMilliGrams(weight)} · {purchaseCountLabel(supplier.batches.length)}</span></div>
        </div>
      </header>
      <div className="flex-1 overflow-y-auto px-4 pb-28 pt-4">
        <div className="mb-2 flex items-center justify-between"><h2 className="text-sm font-semibold">Закупки</h2><span className="text-xs text-ink-faint">{supplier.batches.length}</span></div>
        {supplier.batches.length === 0 ? <div className="rounded-xl border border-dashed border-rule-strong px-5 py-12 text-center"><p className="text-sm font-medium">Закупок пока нет</p><p className="mt-1 text-xs text-ink-muted">Добавьте первый вес кнопкой снизу.</p></div> : <ol className="overflow-hidden rounded-xl border border-rule bg-paper">
          {supplier.batches.map((batch) => {
            const batchAmount = amountFor(BigInt(batch.weightMilliGrams), batch.priceKopecksPerGram)
            const author = 'Автор сохранён в истории'
            const created = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(batch.createdAt))
            return <li key={batch.id} className="relative min-h-16 overflow-hidden border-b border-rule last:border-b-0">
              <button type="button" aria-label={`Удалить закупку ${formatMilliGrams(BigInt(batch.weightMilliGrams))}`} onClick={() => void deleteBatch(batch)} className="absolute inset-y-0 right-0 w-[88px] bg-red-600 text-sm font-semibold text-white">Удалить</button>
              <div
                data-batch-row={batch.id}
                onTouchStart={(event) => { swipeStartX.current = event.touches[0]?.clientX ?? null }}
                onTouchEnd={(event) => {
                  const start = swipeStartX.current
                  const end = event.changedTouches[0]?.clientX
                  swipeStartX.current = null
                  if (start === null || end === undefined) return
                  if (end - start < -44) setSwipedBatchId(batch.id)
                  if (end - start > 32) setSwipedBatchId(null)
                }}
                className={`relative z-10 flex min-h-16 items-center bg-paper transition-transform duration-200 ${swipedBatchId === batch.id ? '-translate-x-[88px]' : 'translate-x-0'}`}
              >
                <button type="button" onClick={() => navigateToBatchEditor(prototypePath(exhibition.id, 'suppliers', supplier.id, 'batches', batch.id, 'edit'), 'prototype-edit-batch-weight')} className="flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-3 text-left active:bg-bone/60">
                  <span className="min-w-0"><strong className="block text-[18px] font-semibold tabular-nums">{formatMilliGrams(BigInt(batch.weightMilliGrams))}</strong><small className="block text-[11px] text-ink-faint">{new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(new Date(batch.createdAt))}</small></span>
                  <strong className="shrink-0 text-[17px] tabular-nums">{batchAmount === null ? '—' : formatKopecks(batchAmount)}</strong>
                </button>
                <BatchActionsMenu author={author} created={created} onEdit={() => navigateToBatchEditor(prototypePath(exhibition.id, 'suppliers', supplier.id, 'batches', batch.id, 'edit'), 'prototype-edit-batch-weight')} onDelete={() => void deleteBatch(batch)} />
              </div>
            </li>
          })}
        </ol>}
      </div>
      <input
        ref={batchKeyboardPrimerRef}
        type="text"
        inputMode="decimal"
        tabIndex={-1}
        aria-label="Подготовка ввода веса закупки"
        className="pointer-events-none fixed bottom-0 left-1/2 h-px w-px opacity-[0.01]"
      />
      <BottomAction
        label="Добавить закупку"
        keyboardInset={keyboardInset}
        onClick={() => navigateToBatchEditor(prototypePath(exhibition.id, 'suppliers', supplier.id, 'new'), 'prototype-batch-weight')}
      />
      <DestructiveConfirmationDialog action={confirmation?.kind === 'delete-batch' ? confirmation : null} busy={mutationPending} error={mutationError} onCancel={() => setConfirmation(null)} onConfirm={() => void confirmDestructiveAction()} />
    </section>
  }

  const budget = BigInt(exhibition.budgetKopecks)
  const remaining = budget - exhibitionTotals.amount
  return <section className="mx-auto flex h-full w-full max-w-xl flex-col overflow-hidden bg-ivory" aria-label="Закупки выставки">
    <header className="shrink-0 border-b border-rule bg-paper px-2 pb-4 pt-3 md:pt-0">
      <div className="hidden min-h-14 items-center gap-2 md:flex"><BackButton onClick={() => navigate(GOLD_EXHIBITION_ROOT)} /><div className="min-w-0 flex-1"><h1 className="truncate text-[24px] font-semibold">{exhibition.name}</h1><p className="truncate text-[13px] font-medium text-ink-muted">{exhibitionDateLabel(exhibition.startsOn, exhibition.endsOn)}</p></div><button type="button" aria-label="Настройки выставки" title="Настройки выставки" onClick={() => navigate(prototypePath(exhibition.id, 'settings'))} className="grid size-11 shrink-0 place-items-center rounded-full border border-rule bg-paper text-ink-muted active:bg-bone"><SettingsIcon /></button></div>
      <div className="mx-2 rounded-2xl bg-onyx p-4 text-white shadow-sm">
        <div className="md:flex md:items-end md:justify-between md:gap-3"><div><p className="text-[13px] text-white/85">Потрачено</p><p className="mt-0.5 whitespace-nowrap text-[24px] font-semibold tabular-nums">{formatKopecks(exhibitionTotals.amount)}</p></div><p className={`mt-1 whitespace-nowrap text-[16px] tabular-nums md:mt-0 md:text-right ${budget > 0 && remaining < 0 ? 'text-red-300' : 'text-white/85'}`}>{budget === BigInt(0) ? 'Задайте бюджет' : remaining < 0 ? `Превышен на ${formatKopecks(-remaining)}` : `Осталось ${formatKopecks(remaining)}`}</p></div>
        <div data-supplier-budget-bar role="img" aria-label="Распределение бюджета по поставщикам" className="mt-3 flex h-1.5 overflow-hidden rounded-full bg-white/15">{exhibition.suppliers.map((item, index) => { const amount = supplierAmount(item); const width = budget === BigInt(0) ? 0 : Math.min(100, Number((amount * BigInt(10000)) / budget) / 100); return <span key={item.id} aria-label={`${item.name}: ${formatKopecks(amount)}`} title={`${item.name}: ${formatKopecks(amount)}`} className="h-full shrink-0" style={{ width: `${width}%`, backgroundColor: SUPPLIER_COLORS[index % SUPPLIER_COLORS.length] }} /> })}</div>
        <div className="mt-3 flex justify-between gap-3 text-[13px] tabular-nums text-white/85"><span className="whitespace-nowrap">{budget === BigInt(0) ? 'Бюджет не задан' : `Бюджет ${formatKopecks(budget)}`}</span><span className="whitespace-nowrap">{formatMilliGrams(exhibitionTotals.weight)}</span></div>
      </div>
    </header>
    <div className="flex-1 overflow-y-auto px-4 pb-28 pt-4">
      <div className="mb-2 flex items-center justify-between"><h1 className="text-[16px] font-semibold">Поставщики</h1><span className="text-[13px] text-ink-muted">Нажмите, чтобы открыть</span></div>
      {exhibition.suppliers.length === 0 ? <div className="rounded-xl border border-dashed border-rule-strong px-5 py-12 text-center"><p className="text-[16px] font-medium">Поставщиков пока нет</p><p className="mt-1 text-[13px] text-ink-muted">Создайте первого и укажите его цену за грамм.</p></div> : <ul className="space-y-2">
        {exhibition.suppliers.map((item, index) => { const share = item.manufacturerAnalyticsKey === null ? undefined : manufacturerSalesShares.get(item.manufacturerAnalyticsKey); return <li key={item.id} data-supplier-card className={`overflow-hidden rounded-xl border bg-paper shadow-[0_1px_2px_rgba(20,17,13,0.04)] ${item.priceKopecksPerGram === null ? 'border-red-200' : 'border-rule'}`}>
          <button type="button" onClick={() => navigate(prototypePath(exhibition.id, 'suppliers', item.id))} className="w-full px-4 py-3 text-left active:bg-bone/60">
            <span className="flex items-center justify-between gap-3"><strong className="flex min-w-0 items-center gap-2 truncate text-[16px]"><span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: SUPPLIER_COLORS[index % SUPPLIER_COLORS.length] }} />{item.name}</strong><span className="flex shrink-0 items-center gap-2 text-[13px] text-ink-muted"><span>{purchaseCountLabel(item.batches.length)}</span><span aria-hidden="true" className="text-[16px]">›</span></span></span>
            {share !== undefined && <span data-supplier-sales-share className="mt-1 block text-[12px] text-ink-muted">Доля продаж за 5 мес. — {formatManufacturerSalesShare(share)}</span>}
            <span data-supplier-details className="mt-2 flex flex-wrap items-center gap-2 tabular-nums"><span className={`whitespace-nowrap rounded-full bg-bone px-2.5 py-1 text-[13px] ${item.priceKopecksPerGram === null ? 'font-semibold text-st-unpaid' : 'text-ink-2'}`}>{item.priceKopecksPerGram === null ? 'Цена не указана' : supplierRateLabel(item.priceKopecksPerGram)}</span><span className="whitespace-nowrap rounded-full bg-bone px-2.5 py-1 text-[13px] text-ink-2">{formatMilliGrams(supplierWeight(item))}</span><strong className="ml-auto whitespace-nowrap text-[16px] font-semibold">{formatKopecks(supplierAmount(item))}</strong></span>
          </button>
          {item.priceKopecksPerGram === null && <div className="flex items-center justify-between gap-3 border-t border-red-100 bg-red-50 px-4 py-2"><span className="text-[13px] font-medium text-st-unpaid">Не указана цена закупки за грамм</span><button type="button" aria-label={`Указать цену для ${item.name}`} onClick={() => navigate(prototypePath(exhibition.id, 'suppliers', item.id, 'settings'))} className="min-h-9 rounded-lg bg-onyx px-3 text-[13px] font-semibold text-white">Указать цену</button></div>}
        </li> })}
      </ul>}
    </div>
    <input ref={supplierKeyboardPrimerRef} type="text" tabIndex={-1} aria-label="Подготовка ввода поставщика" className="pointer-events-none fixed bottom-0 left-1/2 h-px w-px opacity-[0.01]" />
    <BottomAction label="Добавить поставщика" keyboardInset={keyboardInset} onClick={() => navigateToSupplierCreation(prototypePath(exhibition.id, 'suppliers', 'new'))} />
  </section>
}
